<template>
    <div>
        <worksheet-general-information ref="worksheetGeneralInformation"
                                       :api-base-url="apiBaseUrl"
                                       :images-url="imagesUrl"
                                       :file-download-url="fileDownloadUrl"
                                       :current-user-account-id="currentUserAccountId"
                                       :work-item-id="workItemId"
                                       :work-item="workItem"
                                       :related-court-sitting-id="relatedCourtSittingId"
                                       :related-court-matter-id="relatedCourtMatterId"
                                       :related-interview-id="relatedInterviewId"
                                       :related-list-court-id="relatedListCourtId"
                                       :assignment-relevant-roles="assignmentRelevantRoles"
                                       :assignment-roles-with-audio-minutes="assignmentRolesWithAudioMinutes"
                                       :assignment-roles-with-round-robin-priority="assignmentRolesWithRoundRobinPriority"
                                       :all-audio-sub-status-options="allAudioSubStatusOptions"
                                       :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                                       :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                                       :all-deposit-sub-status-options="allDepositSubStatusOptions"
                                       :all-vetting-sub-status-options="allVettingSubStatusOptions"
                                       :portal-upload-required="portalUploadRequired"
                                       :readonly="readonly"
                                       :assignment-option="assignmentOption"
                                       :live-assignment-date="liveAssignmentDate"
                                       :live-assignment-start-time="liveAssignmentStartTime"
                                       :live-assignment-end-time="liveAssignmentEndTime"
                                       :delayed-assignment-start-date="delayedAssignmentStartDate"
                                       :delayed-assignment-due-date="delayedAssignmentDueDate"                                 
                                       :court-registry-id="courtRegistryId"
                                       :interview-organisation-id="interviewOrganisationId"
                                       :time-zone-id="timeZoneId"
                                       :default-live-assignment-response-time="defaultLiveAssignmentResponseTime"
                                       :default-delayed-assignment-response-time="defaultDelayedAssignmentResponseTime"
                                       :all-default-response-time-options="allDefaultResponseTimeOptions"
                                       :all-attachment-classification-options="mainAttachmentClassificationOptions"
                                       v-model="generalData"
                                       @modalOpen="setWorksheetReloadStatus">

            <template v-slot:additional-information>
                <b-form-row v-if="generalData.permissions.canViewAllDetails && workItem && (workItem.completedDateTimeUTC || workItem.recordingCompletedDateTimeUTC)">
                    <b-form-group label="Totals"
                                  label-for="totals"
                                  class="col-md-4">
                        <b-input-group>
                            <b-form-input name="totals"
                                          :value="transcriptTotals"
                                          readonly>
                            </b-form-input>
                            <b-input-group-append v-if="generalData.permissions.canModifyPostCompletion && !readonly">
                                <b-button variant="outline-secondary" v-b-tooltip title="Edit Totals" v-on:click="openEditTranscriptTotalsDialog"><i class="fas fa-edit"></i></b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-form-row>
            </template>

            <recorded-segments ref="recordedSegments"
                               :api-controller-url="apiControllerUrl('recordedsegment')"
                               :work-item-id="workItemId"
                               :work-item-start-time="workItem.startTime"
                               :all-adjournment-types="allAdjournmentTypes"
                               :final-adjournment-type="finalAdjournmentType"
                               :pre-post-recording-adjournment-type="prePostRecordingAdjournmentType"
                               :modify-only="recordedSegmentsModifyOnly"
                               :readonly="recordedSegmentsReadonly"
                               v-on:change="onRecordedSegmentsChanged"
                               v-if="(workItem != null) && (workItem.startTime != null)"
                               @modalOpen="setWorksheetReloadStatus">
            </recorded-segments>

            <template v-slot:action-buttons>
                <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                    <div class="btn-group" role="group">
                        <b-button variant="outline-secondary"
                                  key="startrecording"
                                  v-on:click="openStartEndRecordingWorkItemDialog('start')"
                                  v-if="actionAvailable('start')">
                            <i class="fas fa-play mr-1"></i>Enter Sitting Times
                        </b-button>
                        <b-button variant="outline-secondary"
                                  key="continuerecording"
                                  v-on:click="startWork"
                                  v-if="actionAvailable('continue')">
                            <i class="fas fa-play mr-1"></i>Enter Sitting Times
                        </b-button>
                        <b-button variant="outline-secondary"
                                  key="endrecording"
                                  v-on:click="endWork"
                                  v-if="actionAvailable('end')">
                            <i class="fas fa-stop mr-1"></i>End Recording
                        </b-button>
                        <b-button variant="outline-success"
                                  key="complete"
                                  v-on:click="openStartEndRecordingWorkItemDialog('complete')"
                                  v-if="actionAvailable('complete')">
                            <i class="fas fa-check mr-1"></i>Complete Recording
                        </b-button>
                        <b-button variant="outline-secondary"
                                  key="deliver"
                                  v-on:click="openStartEndRecordingWorkItemDialog('deliver')"
                                  v-if="actionAvailable('deliver')">
                            <i class="fas fa-paper-plane mr-1"></i>Mark Delivered
                        </b-button>
                        <b-button variant="outline-secondary"
                                  key="markuploaded"
                                  v-on:click="markUploaded"
                                  v-if="actionAvailable('markuploaded')">
                            <i class="fas fa-upload mr-1"></i>Mark Uploaded
                        </b-button>
                        <b-button :href="returnUrl" variant="outline-secondary"><i class="fas fa-arrow-left mr-1"></i>Back</b-button>
                    </div>
                </div>
            </template>
        </worksheet-general-information>

        <validation-observer v-slot="vo" slim>
            <b-modal size="xl"
                     centered
                     :title="startEndRecordingWorkItemDialogTitle"
                     v-model="startEndRecordingData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(startEndRecordingWorkItem)">
                <b-alert :show="((startEndRecordingData.currentErrors != null) && (startEndRecordingData.currentErrors.length > 0))" variant="danger" dismissible>
                    <div v-for="(error, index) in startEndRecordingData.currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row v-if="(startEndRecordingData.action != 'deliver')">
                    <validation-provider mode="passive" rules="required" name="Start Time" v-slot="vp" slim>
                        <b-form-group label="Start Time"
                                      label-for="startTime"
                                      class="col">
                            <b-form-input ref="startEndRecordingStartTime"
                                          name="startTime"
                                          type="time"
                                          step="1"
                                          v-model="startEndRecordingData.startTime"
                                          :state="vp.errors[0] ? false : null"
                                          :readonly="(startEndRecordingData.action == 'complete')"
                                          :autofocus="(startEndRecordingData.action == 'start')">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="End Time" v-slot="vp" slim v-if="(startEndRecordingData.action == 'complete')">
                        <b-form-group label="End Time"
                                      label-for="endTime"
                                      class="col">
                            <b-form-input ref="startEndRecordingEndTime"
                                          name="endTime"
                                          type="time"
                                          step="1"
                                          v-model="startEndRecordingData.endTime"
                                          :state="vp.errors[0] ? false : null"
                                          :autofocus="(startEndRecordingData.action == 'complete')">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="vp" slim
                                         v-if="(startEndRecordingData.action == 'complete') && $config.transcriptCompletion.enableWordCount">
                        <b-form-group label="Number of Words"
                                      label-for="numberOfWords"
                                      class="col">
                            <b-form-input name="numberOfWords"
                                          type="number"
                                          min="0"
                                          v-model="startEndRecordingData.numberOfWords"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="vp" slim
                                         v-if="(startEndRecordingData.action == 'complete') && $config.transcriptCompletion.enablePageCount">
                        <b-form-group label="Number of Pages"
                                      label-for="numberOfPages"
                                      class="col">
                            <b-form-input name="numberOfPages"
                                          type="number"
                                          min="0"
                                          v-model="startEndRecordingData.numberOfPages"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                         v-if="(startEndRecordingData.action == 'complete') && $config.transcriptCompletion.enableAudioMinutes">
                        <b-form-group label="Audio Length"
                                      label-for="audioLength"
                                      class="col">
                            <audio-length-input name="audioLength"
                                                v-model="startEndRecordingData.numberOfAudioSeconds"
                                                :state="vp.errors[0] ? false : null">
                            </audio-length-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <template v-if="(startEndRecordingData.action == 'complete') || (startEndRecordingData.action == 'deliver')">
                    <p>Are you sure you want to mark the CHECKED orders as delivered?<br><b>This action cannot be undone.</b></p>
                    <workitem-orders :api-base-url="apiBaseUrl"
                                     :work-item-id="workItemId"
                                     for-order-delivery
                                     v-model="startEndRecordingData.ordersToBeDelivered"
                                     :all-audio-sub-status-options="allAudioSubStatusOptions"
                                     :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                                     :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                                     :all-deposit-sub-status-options="allDepositSubStatusOptions"
                                     :all-vetting-sub-status-options="allVettingSubStatusOptions"
                                     :all-delivered-late-reason-options="allDeliveredLateReasonOptions">
                    </workitem-orders>
                </template>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="vo" slim>
            <b-modal size="md"
                     centered
                     title="Update Transcript Totals"
                     v-model="editTranscriptTotalsData.dialogOpen"
                     v-on:ok.prevent="vo.handleSubmit(updateTranscriptTotals)">
                <b-alert :show="((editTranscriptTotalsData.currentErrors != null) && (editTranscriptTotalsData.currentErrors.length > 0))" variant="danger" dismissible>
                    <div v-for="(error, index) in editTranscriptTotalsData.currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enableWordCount">
                        <b-form-group label="Number of Words"
                                      label-for="numberOfWords"
                                      class="col">
                            <b-form-input name="numberOfWords"
                                          type="number"
                                          min="0"
                                          v-model="editTranscriptTotalsData.numberOfWords"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enablePageCount">
                        <b-form-group label="Number of Pages"
                                      label-for="numberOfPages"
                                      class="col">
                            <b-form-input name="numberOfPages"
                                          type="number"
                                          min="0"
                                          v-model="editTranscriptTotalsData.numberOfPages"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enableAudioMinutes">
                        <b-form-group label="Audio Length"
                                      label-for="audioLength"
                                      class="col">
                            <audio-length-input name="audioLength"
                                                v-model="editTranscriptTotalsData.numberOfAudioSeconds"
                                                :state="vp.errors[0] ? false : null">
                            </audio-length-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import Visibility from 'visibilityjs';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required, integer, min_value, double } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('integer', integer);
    extend('min_value', min_value);
    extend('double', double);
    localize('en', en);

    import AudioLengthInput from '../AudioLengthInput';

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import TranscriptMixins from '../../mixins/TranscriptMixins';
    import WorksheetGeneralInformation from './WorksheetGeneralInformation.vue'
    import RecordedSegments from '../RecordedSegments.vue'
    
    Vue.use(BootstrapVue);
    Vue.component('audio-length-input', AudioLengthInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('worksheet-general-information', WorksheetGeneralInformation);
    Vue.component('recorded-segments', RecordedSegments);
    
    export default {
        name: 'RealtimeWorksheet',
        mixins: [ErrorsMixin, DateTimeMixin, TranscriptMixins],

        props: {
            returnUrl: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            imagesUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            workItemId: {
                type: String,
                required: true
            },
            relatedCourtSittingId: {
                type: String,
                default: null
            },
            relatedCourtMatterId: {
                type: String,
                default: null
            },
            relatedInterviewId: {
                type: String,
                default: null
            },
            relatedListCourtId: {
                type: String,
                default: null
            },
            assignmentRelevantRoles: {
                type: Array,
                required: true
            },
            assignmentRolesWithAudioMinutes: {
                type: Array,
                required: true
            },
            assignmentRolesWithRoundRobinPriority: {
                type: Array,
                required: true
            },
            allAdjournmentTypes: {
                type: Array,
                required: true
            },
            finalAdjournmentType: {
                type: Number,
                required: true
            },
            prePostRecordingAdjournmentType: {
                type: Number,
                required: true
            },
            portalUploadRequired: {
                type: Boolean,
                default: false,
            },
            initialPermissions: {
                type: Object,
                default: null
            },
            refreshIntervalInMilliseconds: {
                type: Number,
                default: 5000
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            allDeliveredLateReasonOptions: {
                type: Array,
                default: null
            },
            readonly: {
                type: Boolean,
                default: false
            },
            assignmentOption: {
                type: String,
                required: false
            },
            liveAssignmentDate: {
                type: Date,
                required: false,
            },
            liveAssignmentStartTime: {
                type: Date,
                required: false,
            },
            liveAssignmentEndTime: {
                type: Date,
                required: false,
            },
            delayedAssignmentStartDate: {
                type: Date,
                required: false,
            },
            delayedAssignmentDueDate: {
                type: Date,
                required: false,
            },
            courtRegistryId: {
                type: String,
                required: false,
                default: null
            },
            interviewOrganisationId: {
                type: String,
                required: false,
                default: null
            },
            timeZoneId: {
                type: String,
                required: false,
                default: null
            },
            defaultLiveAssignmentResponseTime: {
                type: Number
            },
            defaultDelayedAssignmentResponseTime: {
                type: Number
            },
            allDefaultResponseTimeOptions: {
                type: Array,
                default: null
            },
            mainAttachmentClassificationOptions: {
                type: Array,
                default: null
            }
        },
        data: function () {
            return {
                worksheetReloadStatus: false, // flag used to indicate if a modal is open and to pause auto reload()
                customMessages: {
                    audioMinutes: {
                        double: 'must be a number with only 1 decimal'
                    }

                },
                generalData: {
                    permissions: this.initialPermissions ? this.initialPermissions : {},
                    assignedStaff: []
                },
                workItem: null,
                segmentCount: 0,
                lastSegmentEndTime: null,
                lastSegmentAdjournmentType: null,
                startEndRecordingData: {
                    startTime: null,
                    endTime: null,
                    numberOfWords: null,
                    numberOfAudioSeconds: null,
                    numberOfPages: null,
                    ordersToBeDelivered: null,
                    action: null,
                    dialogOpen: false,
                    currentErrors: null
                },
                editTranscriptTotalsData: {
                    numberOfWords: null,
                    numberOfAudioSeconds: null,
                    numberOfPages: null,
                    dialogOpen: false,
                    currentErrors: null
                },
                timer: null,
                reloadPending: false
            };
        },
        computed: {
            recordedSegmentsModifyOnly: function () {
                if ((this.workItem.completedDateTimeUTC == null) && (this.workItem.recordingCompletedDateTimeUTC == null)) return false;
                if (this.generalData.permissions.canModifyPostCompletion && !this.readonly) return true;
                return false;
            },
            recordedSegmentsReadonly: function () {
                if (this.workItem == null) return true;
                if (this.readonly) return true;
                if ((this.workItem.completedDateTimeUTC == null) && (this.workItem.recordingCompletedDateTimeUTC == null)) {
                    return (this.workItem.inProgressByUserAccountId != this.currentUserAccountId);
                }
                return !this.generalData.permissions.canModifyPostCompletion;
            },
            startEndRecordingWorkItemDialogTitle: function () {
                if (this.startEndRecordingData.action == 'start') return 'Enter sitting times';
                if (this.startEndRecordingData.action == 'complete') return 'Complete Recording';
                if (this.startEndRecordingData.action == 'deliver') return 'Deliver Transcript';
                return '';
            },
            transcriptTotals: function () {
                var totals = '';
                if (this.workItem && (this.workItem.completedDateTimeUTC || this.workItem.recordingCompletedDateTimeUTC)) {
                    totals = this.workItem.numberOfWords + ' words / ' + this.getNumberOfFolios(this.workItem.numberOfWords) + ' folios' + ' on ' + this.workItem.numberOfPages + ' pages';
                    if (this.workItem.numberOfAudioSeconds) {
                        var audioTimeDisplay = this.formatSecondsAsDuration(this.workItem.numberOfAudioSeconds);
                        totals += ' for ' + audioTimeDisplay;
                    }
                        
                }
                return totals;
            },
        },
        methods: {

            setWorksheetReloadStatus: function (anyInnerModalOpen) {
                this.worksheetReloadStatus = anyInnerModalOpen;
                this.reload();
            },

            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },

            reload: function (reloadSubComponents = true) {
                if (this.timer != null) {
                    Visibility.stop(this.timer);
                    this.timer = null;
                }
                if (this.reloadPending) return;
                this.reloadPending = true;
                var promises = [];
                var recordedSegmentsVisible = (this.workItem != null) && (this.workItem.startTime != null);
                promises.push(this.loadWorkItem());
                if (reloadSubComponents) {
                    if (recordedSegmentsVisible) promises.push(this.$refs.recordedSegments.reload());
                    promises.push(this.$refs.worksheetGeneralInformation.reload());
                }
                Promise
                    .all(promises)
                    .then(() => {
                        this.reloadPending = false;

                        if (!this.startEndRecordingData.dialogOpen && !this.worksheetReloadStatus) {
                            console.log('reloading');
                            this.timer = Visibility.every(this.refreshIntervalInMilliseconds, this.reload);
                        } else {
                            console.log('reloading PAUSED');
                        }
                    })
            },

            loadWorkItem: function () {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId;
                return axios
                    .get(url)
                    .then(response => {
                        this.workItem = response.data;
                        if (this.workItem.startTime) this.workItem.startTime = this.parseDateTime(this.workItem.startTime, null, false, false)
                        if (this.workItem.endTime) this.workItem.endTime = this.parseDateTime(this.workItem.endTime, null, false, false)
                        if (this.workItem.inProgressSinceDateTimeUTC) this.workItem.inProgressSinceDateTimeUTC = this.parseDateTime(this.workItem.inProgressSinceDateTimeUTC, null, true, true)
                        if (this.workItem.requiredDateTimeUTC) this.workItem.requiredDateTimeUTC = this.parseDateTime(this.workItem.requiredDateTimeUTC, null, true, true)
                        if (this.workItem.lastUpdateDateTimeUTC) this.workItem.lastUpdateDateTimeUTC = this.parseDateTime(this.workItem.lastUpdateDateTimeUTC, null, true, true)
                        if (this.workItem.recordingCompletedDateTimeUTC) this.workItem.recordingCompletedDateTimeUTC = this.parseDateTime(this.workItem.recordingCompletedDateTimeUTC, null, true, true)
                        if (this.workItem.uploadedDateTimeUTC) this.workItem.uploadedDateTimeUTC = this.parseDateTime(this.workItem.uploadedDateTimeUTC, null, true, true)
                        if (this.workItem.completedDateTimeUTC) this.workItem.completedDateTimeUTC = this.parseDateTime(this.workItem.completedDateTimeUTC, null, true, true)
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            actionAvailable: function (action) {
                if (this.workItem == null) return false;
                if (this.workItem.completedDateTimeUTC) return false;
                if (action == 'deliver') {
                    if (this.workItem.recordingCompletedDateTimeUTC == null) return false;
                    if (!this.workItem.deliveryPending) return false;
                    return this.generalData.permissions.canDeliver;
                }
                if (action == 'markuploaded') {
                    if (!this.portalUploadRequired) return false;
                    if (this.workItem.recordingCompletedDateTimeUTC == null) return false;
                    if (this.workItem.uploadedDateTimeUTC != null) return false;
                    return this.generalData.permissions.canMarkUploaded;
                }
                if (this.workItem.recordingCompletedDateTimeUTC) return false;
                if (action == 'start') {
                    if (this.workItem.startTime != null) return false;
                    return this.generalData.permissions.canStartWork;
                } else if (action == 'continue') {
                    if (this.workItem.startTime == null) return false;
                    if (this.workItem.inProgressByUserAccountId != null) return false;
                    return this.generalData.permissions.canStartWork;
                } else if (action == 'end') {
                    if (this.workItem.inProgressByUserAccountId == null) return false;
                    if (this.generalData.permissions.canAlwaysEndWork) return true;
                    return (this.workItem.inProgressByUserAccountId == this.currentUserAccountId);
                } else if (action == 'complete') {
                    if (this.workItem.startTime == null) return false;
                    if (this.lastSegmentEndTime == null) return false;
                    if (this.lastSegmentAdjournmentType != this.finalAdjournmentType && this.lastSegmentAdjournmentType != this.prePostRecordingAdjournmentType) return false;
                    if (this.lastSegmentAdjournmentType == this.prePostRecordingAdjournmentType && this.segmentCount < 2) return false;
                    return (this.workItem.inProgressByUserAccountId == this.currentUserAccountId);
                }
                return false;
            },
            startWork: function () {
                var data = {
                    action: 'startWork'
                };
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId + '/status';
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(false);
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            endWork: function () {
                var data = {
                    action: 'endWork'
                };
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/' + this.workItemId + '/status';
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload(false);
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            openStartEndRecordingWorkItemDialog: function (action) {
                this.startEndRecordingData.action = action;
                this.startEndRecordingData.startTime = this.formatDateTime(this.workItem.startTime, 'HH:mm:ss', false, false);
                this.startEndRecordingData.endTime = this.formatDateTime(this.lastSegmentEndTime, 'HH:mm:ss', false, false);
                this.startEndRecordingData.numberOfWords = this.workItem.numberOfWords;
                this.startEndRecordingData.numberOfAudioSeconds = this.workItem.numberOfAudioSeconds;
                this.startEndRecordingData.numberOfPages = this.workItem.numberOfPages;
                this.startEndRecordingData.ordersToBeDelivered = null;
                this.startEndRecordingData.currentErrors = [];
                this.startEndRecordingData.dialogOpen = true;
            },
            startEndRecordingWorkItem: function () {
                var data = {
                    startTime: this.parseDateTime(this.startEndRecordingData.startTime, 'HH:mm:ss'),
                    endTime: this.parseDateTime(this.startEndRecordingData.endTime, 'HH:mm:ss'),
                    numberOfWords: this.startEndRecordingData.numberOfWords,
                    numberOfPages: this.startEndRecordingData.numberOfPages,
                    numberOfAudioSeconds: this.startEndRecordingData.numberOfAudioSeconds
                };
                this.startEndRecordingData.currentErrors = [];
                if ((this.startEndRecordingData.action == "complete") ||
                    (this.startEndRecordingData.action == "deliver")) {

                    if ((this.startEndRecordingData.action == "deliver") &&
                        ((this.startEndRecordingData.ordersToBeDelivered == null) ||
                         (this.startEndRecordingData.ordersToBeDelivered.length == 0))) {
                        this.startEndRecordingData.currentErrors.push("At least one order has to be selected.");
                    }

                    data.deliveredOrders = [];
                    if (this.startEndRecordingData.ordersToBeDelivered != null) {
                        this.startEndRecordingData.ordersToBeDelivered.forEach((orderToBeDelivered) => {
                            if (orderToBeDelivered.deliveredOnTime) {
                                orderToBeDelivered.deliveredLateReason = null;
                                orderToBeDelivered.deliveredLateReasonText = null;
                            } else {
                                if ((orderToBeDelivered.deliveredLateReason == null) || (orderToBeDelivered.deliveredLateReason == "")) {
                                    this.startEndRecordingData.currentErrors.push("A Late Reason has to be selected for each order not delivered on time.");
                                } else if (orderToBeDelivered.deliveredLateReason == 'Other') {
                                    if ((orderToBeDelivered.deliveredLateReasonText == null) || (orderToBeDelivered.deliveredLateReasonText == "")) {
                                        this.startEndRecordingData.currentErrors.push("A Late Reason Text/Comment has to be specified for each order with Late Reason 'Other'.");
                                    }
                                } 
                            }
                            data.deliveredOrders.push(
                                {
                                    orderId: orderToBeDelivered.orderId,
                                    deliveredOnTime: orderToBeDelivered.deliveredOnTime,
                                    deliveredLateReason: orderToBeDelivered.deliveredLateReason,
                                    deliveredLateReasonText: orderToBeDelivered.deliveredLateReasonText,
                                }
                            );
                        });
                    }
                }
                if (this.startEndRecordingData.currentErrors.length == 0) {
                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'workitem/realtime/' + this.workItemId + '/' + this.startEndRecordingData.action;
                    axios
                        .put(url, JSON.stringify(data),
                            {
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            })
                        .then(() => {
                            this.reload();
                        })
                        .catch(ex => {
                            this.startEndRecordingData.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.startEndRecordingData.currentErrors.length == 0) {
                                this.startEndRecordingData.dialogOpen = false;
                            }
                        });
                }
            },
            markUploaded: function (askForConfirmation = true) {
                if (askForConfirmation) {
                    this.$bvModal.msgBoxConfirm("Are you sure you want to mark the transcript as uploaded?",
                        {
                            title: 'Mark Uploaded',
                            size: 'md',
                            okVariant: 'danger',
                            okTitle: 'Yes',
                            cancelTitle: 'No',
                            centered: true
                        })
                        .then(value => {
                            if (value) {
                                this.markUploaded(false);
                            }
                        });
                } else {
                    var url = this.apiBaseUrl;
                    if (!url.endsWith('/')) url += '/';
                    url += 'workitem/realtime/' + this.workItemId + '/markuploaded';
                    axios
                        .get(url)
                        .then(() => {
                            this.reload(false);
                        })
                        .catch(ex => {
                            var currentErrors = this.handleAxiosException(ex);
                            this.showErrorsToast(currentErrors);
                        });
                }
            },
            openEditTranscriptTotalsDialog: function () {
                this.editTranscriptTotalsData.numberOfWords = this.workItem.numberOfWords;
                this.editTranscriptTotalsData.numberOfAudioSeconds = this.workItem.numberOfAudioSeconds;
                this.editTranscriptTotalsData.numberOfPages = this.workItem.numberOfPages;
                this.editTranscriptTotalsData.currentErrors = [];
                this.editTranscriptTotalsData.dialogOpen = true;
            },
            updateTranscriptTotals: function () {
                var data = {
                    numberOfWords: this.editTranscriptTotalsData.numberOfWords,
                    numberOfAudioSeconds: this.editTranscriptTotalsData.numberOfAudioSeconds,
                    numberOfPages: this.editTranscriptTotalsData.numberOfPages
                };
                this.editTranscriptTotalsData.currentErrors = [];
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += 'workitem/realtime/' + this.workItemId + '/updatetotals';
                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.editTranscriptTotalsData.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.editTranscriptTotalsData.currentErrors.length == 0) {
                            this.editTranscriptTotalsData.dialogOpen = false;
                        }
                    });
            },
            onRecordedSegmentsChanged: function (segmentCount, lastSegmentEndTime, lastSegmentAdjournmentType) {
                this.segmentCount = segmentCount;
                this.lastSegmentEndTime = lastSegmentEndTime;
                this.lastSegmentAdjournmentType = lastSegmentAdjournmentType;
            }
        },
        mounted: function () {
            this.reload(false); //each individual sub component will anyway load its data when mounted, no need to reload everything again when mounted
        },
       
        beforeDestroy: function () {
            if (this.timer != null) Visibility.stop(this.timer);
        }
    }
</script>
